/**
 * Quick-filter chips for the NCR list — a compact mode switch above the table
 * for the handful of views people actually jump between. Each chip maps to the
 * NCR viewset's existing filter params (status / next_action / reject). The
 * counts come from one batched request (`ncrs/tab-counts/`) that replays each
 * chip's exact params through the list queryset, so a chip's number is the
 * count the list shows after clicking it. Counts refresh with the surrounding
 * filters/view.
 *
 * "Appeals" = supplier has requested rejection and the NCR is still open
 * (reject=pending on the backend), i.e. the supplier-reject flow awaiting the
 * customer's Accept & Void / Decline decision.
 */
import { useEffect, useState } from 'react';
import { qualityAPI } from '../../lib/api';
import { useView } from '../../contexts/ViewContext';

export type NCRQuickMode = 'all' | 'awaiting_supplier' | 'awaiting_customer' | 'no_actions' | 'appeals' | 'closed' | 'archived';

/** Chip definitions — order is the display order. */
export const NCR_QUICK_MODES: { key: NCRQuickMode; label: string; params: Record<string, string> }[] = [
  { key: 'all', label: 'All', params: {} },
  { key: 'awaiting_supplier', label: 'Awaiting Supplier', params: { next_action: 'awaiting_supplier' } },
  { key: 'awaiting_customer', label: 'Awaiting Customer', params: { next_action: 'awaiting_customer' } },
  // Spec "Action Required By: No Actions Outstanding" -- open or closed NCRs
  // with nothing owed by anyone (deleted excluded). The two Awaiting chips are
  // the spec's Customer / Supplier values, now across every process.
  { key: 'no_actions', label: 'No Actions', params: { next_action: 'none' } },
  { key: 'appeals', label: 'Appeals', params: { reject: 'pending' } },
  { key: 'closed', label: 'Closed', params: { status: 'closed' } },
  // Archived NCRs are hidden from every other chip by default, so this is the
  // only way to reach them. Rendered for customer admins only — they are the
  // only role that can archive or unarchive, and the rows are noise to anyone
  // who cannot act on them. Archiving is list-tidying: these NCRs still count
  // toward PPM and supplier scorecards.
  { key: 'archived', label: 'Archived', params: { archived: 'true' } },
];

/** Chips that only a customer admin should see. */
export const ADMIN_ONLY_QUICK_MODES: ReadonlySet<NCRQuickMode> =
  new Set<NCRQuickMode>(['archived']);

interface NCRQuickFiltersProps {
  /** Currently active quick mode (derived from the parent's filter state). */
  activeMode: NCRQuickMode;
  /** Whether to render the admin-only chips (Archived). */
  isCustomerAdmin?: boolean;
  /**
   * Bumped by the parent after a row action (archive / unarchive / delete)
   * changes which NCRs belong in which chip. The counts otherwise key only
   * off the filter params, which such an action leaves untouched.
   */
  refreshKey?: number;
  /** Apply a mode — the parent sets status/next_action/reject accordingly. */
  onSelect: (mode: NCRQuickMode) => void;
  /**
   * Common list params (search, supplier, date, view context) WITHOUT the
   * mode-specific status/next_action/reject keys — used to keep the chip counts
   * consistent with the rest of the filter bar.
   */
  baseParams: Record<string, unknown>;
}

/**
 * Renders the NCR quick-filter chip row with per-mode counts.
 */
/** The chips this viewer actually gets. Archived is admin-only. */
export function visibleQuickModes(isCustomerAdmin?: boolean) {
  return NCR_QUICK_MODES.filter(
    (m) => isCustomerAdmin || !ADMIN_ONLY_QUICK_MODES.has(m.key),
  );
}

/** The param sets the batched count endpoint replays — one per chip, each the
 *  surrounding filters plus that chip's mode keys (undefined values dropped).
 *
 *  Scoped to the viewer's own chips: asking for a count nobody can click both
 *  wastes a query on the biggest table in the app and hands a non-admin a
 *  number for a list they are not allowed to open. */
export function buildQuickFilterTabs(
  baseParams: Record<string, unknown>,
  isCustomerAdmin?: boolean,
): Record<NCRQuickMode, Record<string, string | number | boolean>> {
  const base: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(baseParams)) {
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') base[k] = v;
  }
  const tabs = {} as Record<NCRQuickMode, Record<string, string | number | boolean>>;
  for (const m of visibleQuickModes(isCustomerAdmin)) tabs[m.key] = { ...base, ...m.params };
  return tabs;
}

export default function NCRQuickFilters({ activeMode, onSelect, baseParams, isCustomerAdmin, refreshKey = 0 }: NCRQuickFiltersProps) {
  const [counts, setCounts] = useState<Partial<Record<NCRQuickMode, number>>>({});
  // baseParams carries the parent's view scope; until ViewContext resolves it
  // would be the unscoped (wrong) scope and the request would only be repeated.
  const { isLoading: viewContextLoading } = useView();
  const baseKey = JSON.stringify(baseParams);

  useEffect(() => {
    if (viewContextLoading) return;
    let cancelled = false;
    const run = async () => {
      try {
        const res = await qualityAPI.getNCRTabCounts(
          buildQuickFilterTabs(baseParams, isCustomerAdmin),
        );
        if (cancelled) return;
        const next: Partial<Record<NCRQuickMode, number>> = {};
        const returned = res.data.counts || {};
        for (const m of visibleQuickModes(isCustomerAdmin)) {
          const count = returned[m.key];
          if (typeof count === 'number') next[m.key] = count;
        }
        setCounts(next);
      } catch {
        /* counts are best-effort; leave the chips unlabelled on failure */
      }
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseKey, viewContextLoading, refreshKey, isCustomerAdmin]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {visibleQuickModes(isCustomerAdmin)
        .map((m) => {
        const isActive = activeMode === m.key;
        const count = counts[m.key];
        const isAppeals = m.key === 'appeals';
        return (
          <button
            key={m.key}
            onClick={() => onSelect(m.key)}
            className={
              `px-3 py-1.5 text-sm rounded-full border transition-colors ` +
              (isActive
                ? isAppeals
                  ? 'bg-amber-500 text-white border-amber-500'
                  : 'bg-primary text-primary-foreground border-primary'
                : 'bg-background text-foreground hover:bg-muted border-border')
            }
          >
            {m.label}
            {count !== undefined && (
              <span className={isActive ? 'ml-1.5 opacity-90' : `ml-1.5 ${isAppeals && count > 0 ? 'font-semibold text-amber-600' : 'text-muted-foreground'}`}>
                ({count.toLocaleString()})
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
